<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import lx from '../../utils/lx'
import { ensureVideoSource } from '../../utils/lazyVideo'
import VideoPlayHint from './VideoPlayHint.vue'
import { CASE_VIDEOS, LX_BIDS, type CaseItem } from './constants'

const props = defineProps<{
  isMobile: boolean
}>()

const MOBILE_INITIAL_INDEX = 1

const trackRef = ref<HTMLElement | null>(null)
const sectionRef = ref<HTMLElement | null>(null)
const scrollerRef = ref<HTMLElement | null>(null)
const modalRef = ref<HTMLElement | null>(null)
const modalVideoRef = ref<HTMLVideoElement | null>(null)

/** 移动端用更小版片源，桌面端保持原片 */
const caseVideoSrc = (item: CaseItem) => (props.isMobile ? item.videoMobile : item.video)
const hoveredKey = ref<string | null>(null)
const paused = ref(false)
const mobileActiveIndex = ref(MOBILE_INITIAL_INDEX)
const mobileModalItem = ref<CaseItem | null>(null)
const mobileModalLoading = ref(false)
const mobileModalError = ref(false)
const mobileModalNeedsTap = ref(false)
/** 原生 controls 默认可能带深色遮罩；先隐藏，用户点击后再展示 */
const modalControlsVisible = ref(false)

/** 无缝轮播：复制一份列表 */
const loopItems = [...CASE_VIDEOS, ...CASE_VIDEOS]
const displayItems = computed(() => props.isMobile ? CASE_VIDEOS : loopItems)

let offset = 0
let rafId = 0
let mobileScrollRaf = 0
let bodyOverflowBeforeModal: string | null = null
let mobilePreloadObserver: IntersectionObserver | undefined
let mobilePreloadEnabled = false
let mobileErrorTimer: number | undefined
let mobileRetryCount = 0
const DESKTOP_SPEED = 0.45

const normalizeOffset = (half: number) => {
  if (half <= 0) return
  while (offset <= -half) offset += half
  while (offset > 0) offset -= half
}

const applyTransform = () => {
  const track = trackRef.value
  if (!track) return
  track.style.transform = `translate3d(${offset}px, 0, 0)`
}

const tick = () => {
  rafId = requestAnimationFrame(tick)
  const track = trackRef.value
  if (props.isMobile || !track || paused.value) return
  const half = track.scrollWidth / 2
  if (half <= 0) return
  offset -= DESKTOP_SPEED
  normalizeOffset(half)
  applyTransform()
}

/** 停在当前帧，供蒙层透出（不自动续播） */
const freezeVideoFrame = (video: HTMLVideoElement | null | undefined) => {
  if (!video) return
  video.dataset.caseActive = '0'
  video.pause()
  video.muted = true
  video.defaultMuted = true
  video.setAttribute('muted', '')
}

/** 桌面端保持原交互：页面挂载后直接加载全部视频画面。 */
const prepareDesktopVideo = (video: HTMLVideoElement, src: string) => {
  video.dataset.lazyLoaded = '1'
  video.src = src
  video.preload = 'auto'
  video.muted = true
  video.defaultMuted = true
  video.setAttribute('muted', '')
  video.load()
}

const pauseAllVideos = (except?: HTMLVideoElement | null) => {
  trackRef.value?.querySelectorAll('video').forEach((node) => {
    if (node === except) return
    freezeVideoFrame(node)
  })
}

const playCardVideo = async (video: HTMLVideoElement | null, src: string) => {
  if (!video) return
  await ensureVideoSource(video, src, { sequential: true })
  video.volume = 0.5
  try {
    video.muted = false
    await video.play()
  } catch {
    try {
      video.muted = true
      await video.play()
    } catch {
      // ignore
    }
  }
}

const activateCard = (key: string, name: string, videoSrc: string, event: Event) => {
  hoveredKey.value = key
  paused.value = true
  const video = (event.currentTarget as HTMLElement).querySelector('video')
  pauseAllVideos(video)
  if (video) video.dataset.caseActive = '1'
  void playCardVideo(video, videoSrc)
  onCardClick(name)
}

const onEnterCard = (key: string, name: string, videoSrc: string, event: Event) => {
  if (props.isMobile) return
  activateCard(key, name, videoSrc, event)
}

/** 桌面端点击兜底；移动端由覆盖整张卡片的按钮打开弹窗。 */
const onCardTap = (key: string, name: string, videoSrc: string, event: Event) => {
  if (props.isMobile) return
  if (hoveredKey.value === key) return
  activateCard(key, name, videoSrc, event)
}

const openMobileModal = (item: CaseItem) => {
  if (!props.isMobile) return
  const video = modalVideoRef.value
  if (!video) return

  mobileModalItem.value = item
  mobileModalLoading.value = true
  mobileModalError.value = false
  mobileModalNeedsTap.value = false
  modalControlsVisible.value = false
  mobileRetryCount = 0
  clearMobileErrorTimer()
  if (modalRef.value) modalRef.value.style.display = 'flex'
  if (bodyOverflowBeforeModal === null) {
    bodyOverflowBeforeModal = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  video.poster = item.poster
  video.preload = 'auto'
  video.autoplay = true
  video.volume = 0.5
  video.muted = false
  video.defaultMuted = false
  video.removeAttribute('muted')
  const hasPreloadedSource = video.dataset.caseItemId === String(item.id) && !video.error
  if (!hasPreloadedSource) {
    // 微信 WebView 对隐藏 video 的预加载兼容性不稳定；点击时清理旧状态再加载，
    // 避免预加载阶段遗留的 MediaError 污染第一次弹窗。
    video.pause()
    video.removeAttribute('src')
    video.load()
    video.dataset.caseItemId = String(item.id)
    video.src = caseVideoSrc(item)
    video.load()
  }

  // 在案例按钮的同步点击手势内发起播放，移动端可直接以有声状态开始。
  playMobileModalWithSound()
  onCardClick(item.name)
}

const playMobileModalWithSound = () => {
  const video = modalVideoRef.value
  if (!mobileModalItem.value || !video) return
  video.volume = 0.5
  video.muted = false
  video.defaultMuted = false
  video.removeAttribute('muted')
  try {
    void video.play().catch((error) => {
      if (!mobileModalItem.value) return
      if (error instanceof DOMException && error.name === 'AbortError') return
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        mobileModalLoading.value = false
        mobileModalNeedsTap.value = true
      } else {
        mobileModalLoading.value = true
      }
      console.warn('[CaseSection] 移动端弹窗有声自动播放失败：', error)
    })
  } catch (error) {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      mobileModalLoading.value = false
      mobileModalNeedsTap.value = true
    } else {
      mobileModalLoading.value = true
    }
    console.warn('[CaseSection] 移动端弹窗有声自动播放失败：', error)
  }
}

const clearMobileErrorTimer = () => {
  if (mobileErrorTimer === undefined) return
  window.clearTimeout(mobileErrorTimer)
  mobileErrorTimer = undefined
}

const onMobileModalLoading = () => {
  if (!mobileModalItem.value) return
  mobileModalLoading.value = true
}

const onMobileModalCanPlay = () => {
  clearMobileErrorTimer()
  mobileRetryCount = 0
  playMobileModalWithSound()
}

const onMobileModalPlaying = () => {
  clearMobileErrorTimer()
  mobileRetryCount = 0
  mobileModalLoading.value = false
  mobileModalError.value = false
  mobileModalNeedsTap.value = false
}

const onMobileModalError = () => {
  if (!mobileModalItem.value) return
  // 弱网及微信分段加载时不按固定等待时间判失败，保持 Loading 并持续重试。
  mobileModalLoading.value = true
  mobileModalError.value = false
  mobileModalNeedsTap.value = false
  clearMobileErrorTimer()
  const retryDelay = Math.min(1200 * (2 ** mobileRetryCount), 5000)
  mobileRetryCount += 1
  mobileErrorTimer = window.setTimeout(() => {
    mobileErrorTimer = undefined
    const video = modalVideoRef.value
    const item = mobileModalItem.value
    if (!item || !video) return
    if (!video.error || video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      playMobileModalWithSound()
      return
    }
    video.pause()
    video.removeAttribute('src')
    video.load()
    video.dataset.caseItemId = String(item.id)
    video.src = caseVideoSrc(item)
    video.load()
    playMobileModalWithSound()
  }, retryDelay)
}

const retryMobileModalPlayback = () => {
  const video = modalVideoRef.value
  if (!video || !mobileModalItem.value) return
  mobileModalLoading.value = true
  mobileModalError.value = false
  mobileModalNeedsTap.value = false
  mobileRetryCount = 0
  clearMobileErrorTimer()
  video.removeAttribute('src')
  video.load()
  video.src = caseVideoSrc(mobileModalItem.value)
  video.load()
  playMobileModalWithSound()
}

const resumeMobileModalPlayback = () => {
  mobileModalLoading.value = true
  mobileModalNeedsTap.value = false
  playMobileModalWithSound()
}

const revealModalControls = () => {
  if (!mobileModalItem.value) return
  if (mobileModalLoading.value || mobileModalError.value || mobileModalNeedsTap.value) return
  modalControlsVisible.value = true
}

const closeMobileModal = () => {
  clearMobileErrorTimer()
  mobileRetryCount = 0
  const video = modalVideoRef.value
  if (video) {
    video.pause()
    video.autoplay = false
    video.preload = 'metadata'
  }
  if (modalRef.value) modalRef.value.style.display = 'none'
  mobileModalItem.value = null
  mobileModalLoading.value = false
  mobileModalError.value = false
  mobileModalNeedsTap.value = false
  modalControlsVisible.value = false
  if (bodyOverflowBeforeModal !== null) {
    document.body.style.overflow = bodyOverflowBeforeModal
    bodyOverflowBeforeModal = null
  }
}

const onLeaveCard = (event: Event) => {
  if (props.isMobile) return
  hoveredKey.value = null
  paused.value = false
  const video = (event.currentTarget as HTMLElement).querySelector('video')
  freezeVideoFrame(video)
}

const onCardClick = (videoName: string) => {
  lx.moduleClick(LX_BIDS.caseMc, { video_name: videoName })
}

const updateMobileActiveIndex = () => {
  mobileScrollRaf = 0
  if (!props.isMobile) return
  const scroller = scrollerRef.value
  if (!scroller) return
  const center = scroller.getBoundingClientRect().left + scroller.clientWidth / 2
  const cards = Array.from(scroller.querySelectorAll<HTMLElement>('.case-media-item'))
  let closestIndex = 0
  let closestDistance = Number.POSITIVE_INFINITY
  cards.forEach((card, index) => {
    const rect = card.getBoundingClientRect()
    const distance = Math.abs(rect.left + rect.width / 2 - center)
    if (distance < closestDistance) {
      closestDistance = distance
      closestIndex = index
    }
  })
  mobileActiveIndex.value = closestIndex
  if (mobilePreloadEnabled) preloadMobileCase(CASE_VIDEOS[closestIndex])
}

const onMobileScroll = () => {
  if (!props.isMobile || mobileScrollRaf) return
  mobileScrollRaf = requestAnimationFrame(updateMobileActiveIndex)
}

const preloadMobileCase = (item: CaseItem | undefined) => {
  const video = modalVideoRef.value
  if (!props.isMobile || !item || !video || mobileModalItem.value) return
  if (video.dataset.caseItemId === String(item.id) && !video.error) return
  video.pause()
  video.autoplay = false
  // 仅提前缓冲当前居中的一个案例；浏览器会通过 HTTP Range 渐进下载。
  video.preload = 'auto'
  video.poster = item.poster
  video.dataset.caseItemId = String(item.id)
  video.src = caseVideoSrc(item)
  video.load()
}

const centerMobileCard = (index: number) => {
  const scroller = scrollerRef.value
  if (!props.isMobile || !scroller) return
  const cards = scroller.querySelectorAll<HTMLElement>('.case-media-item')
  const card = cards[index]
  if (!card) return
  mobileActiveIndex.value = index
  scroller.scrollTo({
    left: card.offsetLeft + card.offsetWidth / 2 - scroller.clientWidth / 2,
    behavior: 'auto'
  })
  if (mobilePreloadEnabled) preloadMobileCase(CASE_VIDEOS[index])
}

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && mobileModalItem.value) closeMobileModal()
}

const bindDesktopVideos = () => {
  if (props.isMobile) return
  const videos = Array.from(
    sectionRef.value?.querySelectorAll<HTMLVideoElement>('video[data-src]') ?? []
  )
  videos.forEach((video) => {
    const src = video.dataset.src
    if (src) prepareDesktopVideo(video, src)
  })
}

onMounted(() => {
  rafId = requestAnimationFrame(tick)
  bindDesktopVideos()
  scrollerRef.value?.addEventListener('scroll', onMobileScroll, { passive: true })
  document.addEventListener('keydown', onDocumentKeydown)
  if (sectionRef.value) {
    mobilePreloadObserver = new IntersectionObserver(
      ([entry]) => {
        mobilePreloadEnabled = Boolean(entry?.isIntersecting)
        if (mobilePreloadEnabled && props.isMobile) {
          preloadMobileCase(CASE_VIDEOS[mobileActiveIndex.value])
        }
      },
      { rootMargin: '480px 0px', threshold: 0 }
    )
    mobilePreloadObserver.observe(sectionRef.value)
  }
  if (props.isMobile) requestAnimationFrame(() => centerMobileCard(MOBILE_INITIAL_INDEX))
})

watch(() => props.isMobile, async (isMobile) => {
  if (!isMobile) closeMobileModal()
  await nextTick()
  if (isMobile) {
    requestAnimationFrame(() => {
      centerMobileCard(MOBILE_INITIAL_INDEX)
      if (mobilePreloadEnabled) preloadMobileCase(CASE_VIDEOS[MOBILE_INITIAL_INDEX])
    })
  } else {
    bindDesktopVideos()
  }
})

onBeforeUnmount(() => {
  cancelAnimationFrame(rafId)
  cancelAnimationFrame(mobileScrollRaf)
  scrollerRef.value?.removeEventListener('scroll', onMobileScroll)
  document.removeEventListener('keydown', onDocumentKeydown)
  mobilePreloadObserver?.disconnect()
  clearMobileErrorTimer()
  closeMobileModal()
})
</script>

<template>
  <section ref="sectionRef" id="caseSection" class="case-section">
    <div class="section-heading">
      <div class="section-title">直播间案例</div>
      <div class="section-sub">从数字人形象到直播场景，稳定呈现专业、自然的直播效果</div>
    </div>

    <div class="case-viewport">
      <div ref="scrollerRef" class="case-track-wrap">
        <div ref="trackRef" class="case-media-row">
          <div
            v-for="(item, index) in displayItems"
            :key="`${item.id}-${index}`"
            class="case-media-item"
            :class="{
              'is-active': !props.isMobile && hoveredKey === `${item.id}-${index}`,
              'is-mobile-active': props.isMobile && mobileActiveIndex === index
            }"
            :data-mv-bid="LX_BIDS.caseMv"
            :data-mv-key="`case-${item.id}-${index}`"
            :data-mv-video-name="item.name"
            @mouseenter="onEnterCard(`${item.id}-${index}`, item.name, item.video, $event)"
            @mouseleave="onLeaveCard($event)"
            @click="onCardTap(`${item.id}-${index}`, item.name, item.video, $event)"
          >
            <img
              class="case-poster"
              :src="item.poster"
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="async"
              draggable="false"
            >
            <video
              v-if="!props.isMobile"
              class="case-video"
              :data-src="item.video"
              :poster="item.poster"
              muted
              loop
              playsinline
              webkit-playsinline
              x5-playsinline
              preload="none"
              controlslist="nodownload noplaybackrate"
              :controls="hoveredKey === `${item.id}-${index}`"
              disablepictureinpicture
              disableremoteplayback
              @click.stop
            />
            <!-- 半透明毛玻璃蒙层 + 居中封面；激活后淡出露出清晰视频 -->
            <div class="case-mask" aria-hidden="true">
              <img class="case-cover" :src="item.cover" :alt="item.name" draggable="false">
            </div>
            <button
              v-if="props.isMobile"
              class="case-play-trigger"
              type="button"
              :aria-label="`播放${item.name}案例视频`"
              @click.stop="openMobileModal(item)"
            >
              <VideoPlayHint />
            </button>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        ref="modalRef"
        v-show="mobileModalItem"
        class="case-video-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="mobileModalItem ? `播放${mobileModalItem.name}案例视频` : '案例视频'"
        @click.self="closeMobileModal"
      >
        <button
          class="case-video-modal-close"
          type="button"
          aria-label="关闭案例视频"
          @click="closeMobileModal"
        >
          <span aria-hidden="true">×</span>
        </button>
        <div class="case-video-modal-content">
          <video
            ref="modalVideoRef"
            class="case-video-modal-player"
            playsinline
            webkit-playsinline
            x5-playsinline
            :controls="modalControlsVisible && !mobileModalLoading && !mobileModalError && !mobileModalNeedsTap"
            autoplay
            controlslist="nodownload noplaybackrate"
            disablepictureinpicture
            disableremoteplayback
            preload="metadata"
            @click="revealModalControls"
            @canplay="onMobileModalCanPlay"
            @loadstart="onMobileModalLoading"
            @waiting="onMobileModalLoading"
            @stalled="onMobileModalLoading"
            @playing="onMobileModalPlaying"
            @error="onMobileModalError"
          />
          <div v-if="mobileModalLoading" class="case-video-modal-status" aria-live="polite">
            <mtd-loading
              class="video-modal-loading"
              size="large"
              message="视频加载中…"
            />
          </div>
          <div
            v-else-if="mobileModalError"
            class="case-video-modal-status case-video-modal-error"
            role="alert"
          >
            <span>视频加载失败，请稍后重试</span>
            <button type="button" @click="retryMobileModalPlayback">重新加载</button>
          </div>
          <div
            v-else-if="mobileModalNeedsTap"
            class="case-video-modal-status case-video-modal-resume"
          >
            <button type="button" @click="resumeMobileModalPlayback">点击播放</button>
          </div>
        </div>
      </div>
    </Teleport>
  </section>
</template>

<style scoped lang="scss">
.case-section {
  width: 100%;
  margin: 160px auto 0;
  overflow: hidden;
}

.section-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 21px;
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 0 auto 48px;
  text-align: center;
}

.section-title {
  color: #8932ff;
  font-size: 48px;
  font-weight: 700;
  line-height: normal;
}

.section-sub {
  color: #666;
  font-size: 18px;
  font-weight: 300; /* L */
  line-height: normal;
}

.case-viewport {
  position: relative;
  touch-action: pan-y;
}

.case-track-wrap {
  overflow: hidden;
  transform: translateZ(0);
}

.case-media-row {
  display: flex;
  gap: 44px;
  width: max-content;
  padding: 0 4px;
  will-change: transform;
}

.case-media-item {
  position: relative;
  isolation: isolate;
  flex: none;
  width: 252px;
  height: 486px;
  overflow: hidden;
  border-radius: 24px;
  background: #eaeaea;
  cursor: pointer;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.case-video {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #eaeaea;
  backface-visibility: hidden;
  transform: translateZ(0);
}

.case-poster {
  position: absolute;
  inset: 0;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  user-select: none;
}

/*
 * 全屏：按同一套片源像素裁切比例铺满，避免 cover 在不同机型上不一致。
 */
.case-video:fullscreen,
.case-video:-webkit-full-screen {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center bottom;
  background: #000;
}

/* 竖卡较窄，控制条贴底展示 */
.case-media-item.is-active .case-video {
  z-index: 1;
}

/* 默认遮罩：封面垂直水平居中 */
.case-mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.49);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  transition: opacity 0.28s ease;
  pointer-events: none;
}

.case-cover {
  display: block;
  width: 80px;
  height: 107px;
  object-fit: contain;
  user-select: none;
}

.case-play-trigger {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

/* hover 播视频：蒙层与封面一起淡出 */
.case-media-item.is-active .case-mask {
  opacity: 0;
}

.case-video-modal {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px 24px 28px;
  background: rgba(26, 29, 35, 0.76);
}

.case-video-modal-content {
  position: relative;
  /* 移动端不再裁切：按片源原始比例展示 */
  width: min(78vw, 360px);
  min-height: min(52vh, 360px);
  max-height: calc(100dvh - 112px);
  overflow: hidden;
  border-radius: 18px;
  background: #111;
  box-shadow: 0 18px 56px rgba(0, 0, 0, 0.32);
}

.case-video-modal-status {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(17, 17, 17, 0.56);
}

.video-modal-loading {
  color: #fff;
}

.case-video-modal-error {
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  text-align: center;
}

.case-video-modal-error button {
  padding: 8px 18px;
  border: 1px solid rgba(255, 255, 255, 0.68);
  border-radius: 18px;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  font: inherit;
}

.case-video-modal-resume {
  background: rgba(17, 17, 17, 0.36);
}

.case-video-modal-resume button {
  padding: 10px 22px;
  border: 1px solid rgba(255, 255, 255, 0.72);
  border-radius: 22px;
  color: #fff;
  background: rgba(0, 0, 0, 0.36);
  font: inherit;
}

.case-video-modal-player {
  display: block;
  width: 100%;
  height: auto;
  max-height: calc(100dvh - 112px);
  object-fit: contain;
  background: #111;
  vertical-align: middle;
}

.case-video-modal-player:fullscreen,
.case-video-modal-player:-webkit-full-screen {
  width: 100%;
  height: 100%;
  max-height: none;
  object-fit: contain;
  background: #000;
}

.case-video-modal-close {
  position: fixed;
  top: max(18px, env(safe-area-inset-top));
  right: 18px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0 0 3px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  font-size: 30px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

@media (max-width: 960px) {
  .case-section {
    margin: 64px 0 0;
  }

  .section-heading {
    width: auto;
    max-width: none;
    margin-bottom: 24px;
    padding: 0 20px;
  }

  .section-title {
    font-size: 28px;
  }

  .case-viewport {
    touch-action: pan-x pan-y;
  }

  .case-track-wrap {
    overflow-x: auto;
    overflow-y: hidden;
    scroll-padding-inline: calc((100vw - 210px) / 2);
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    overscroll-behavior-inline: contain;
    touch-action: pan-x pan-y;
    -webkit-overflow-scrolling: touch;
    transform: none;
  }

  .case-track-wrap::-webkit-scrollbar {
    display: none;
  }

  .case-media-item {
    width: 210px;
    height: 405px;
    border-radius: 18px;
    contain: paint;
    opacity: 0.34;
    scroll-snap-align: center;
    scroll-snap-stop: always;
    transform: scale(0.86) translateZ(0);
    transition: opacity 0.24s ease, transform 0.24s ease, box-shadow 0.24s ease;
  }

  .case-media-item.is-mobile-active {
    opacity: 1;
    transform: scale(1) translateZ(0);
    box-shadow: 0 12px 30px rgba(28, 31, 38, 0.12);
  }

  .case-mask {
    border-radius: 14px;
    /* 只叠静态色层，不实时采样背后的 video，避免安卓 GPU 合成闪烁。 */
    background: rgba(238, 240, 242, 0.34);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    backface-visibility: hidden;
    transform: translateZ(0);
  }

  /* 模糊静态 poster 可被缓存；比 backdrop-filter 每帧重采样视频稳定得多。 */
  .case-poster {
    filter: blur(7px) saturate(0.72) brightness(0.92);
    backface-visibility: hidden;
    transform: scale(1.08) translateZ(0);
  }

  .case-media-row {
    gap: 16px;
    padding: 8px calc((100vw - 210px) / 2) 18px;
    will-change: auto;
    transform: none !important;
  }

  .case-media-item :deep(.video-play-hint) {
    z-index: 4;
  }
}
</style>
